/** Thin fetch wrapper: same-origin cookies, JSON in/out, errors carry the server's message. */
export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
  }
}

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  const res = await fetch(url, {
    method,
    credentials: 'same-origin',
    headers: body === undefined ? {} : { 'content-type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    if (res.status === 401 && !url.startsWith('/auth/') && location.pathname !== '/login' && !location.pathname.startsWith('/m')) {
      location.assign('/login');
    }
    throw new ApiError(res.status, data?.error ?? 'ERROR', data?.message ?? res.statusText, data?.details);
  }
  return data as T;
}

export const api = {
  get: <T>(url: string) => request<T>('GET', url),
  post: <T>(url: string, body: unknown = {}) => request<T>('POST', url, body),
  patch: <T>(url: string, body: unknown) => request<T>('PATCH', url, body),
  put: <T>(url: string, body: unknown) => request<T>('PUT', url, body),
};

/** Human-readable message for any thrown error, including Zod validation details. */
export function errorText(err: unknown): string {
  if (err instanceof ApiError) {
    const issues = (err.details as { path?: (string | number)[]; message: string }[] | undefined) ?? [];
    if (err.code === 'VALIDATION' && Array.isArray(issues) && issues.length) {
      return issues.map((i) => `${(i.path ?? []).join('.') || 'input'}: ${i.message}`).join('; ');
    }
    return err.message;
  }
  return err instanceof Error ? err.message : String(err);
}

export const qs = (params: Record<string, string | number | undefined | null>) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
};
